import { venueSlugOf } from './places';
import { VENUE_FACTS as FACTS } from './venues';
import type { Event, PracticalInfo, RawEvent } from '../types';
import { slugify as slugifyRaw } from '../../shared/slug.mjs';
import { postseasonLabel } from '../../shared/postseason.mjs';

/**
 * RawEvent → Event. Its own module so both the catalog (SSR, full data) and
 * the client store (lazy slices) can build Events without importing each
 * other.
 */

// Practical info is generated from venue type (estimated, clearly labelled as
// such in the UI). Clear-bag rules, ounces, card-only concessions and
// app-based mobile entry describe US venues; an event anywhere else — or one
// whose country we do not know — gets wording that holds everywhere.
const getPracticalInfo = (e: { venueType: RawEvent['venueType']; country?: string }): PracticalInfo => {
  const isStadium = e.venueType === 'Stadium';
  const us = e.country === 'US';
  return {
    ticketsEntry: {
      doorTime: isStadium ? 'typically 1.5-2 hours before start' : 'typically about an hour before start',
      bagPolicy: isStadium
        ? us
          ? 'Most US stadiums enforce a clear-bag policy (roughly 12" x 6" x 12" max)'
          : 'Many stadiums limit bag sizes, and some allow clear bags only - check the venue site before you go'
        : 'Small bags only at most venues - check the venue site before you go',
      idRequired: true,
      ticketScanning: us
        ? 'Mobile entry at most US venues - have the ticket app ready; screenshots are often rejected.'
        : 'Have your ticket ready before the gate - many venues scan it on your phone, and screenshots are often rejected.',
    },
    transport: {
      publicTransport: 'Public transit is usually the fastest way in and out on event nights.',
      parking: 'On-site parking is limited at most major venues and often must be pre-booked.',
      rideshare: us
        ? 'Rideshare pickup zones are typically a short walk from the gates.'
        : 'Taxi and rideshare pickup points are typically a short walk from the gates.',
    },
    weather: {
      type: isStadium ? 'Outdoor' : 'Indoor',
      forecast: 'Check the local forecast a few days out.',
      clothing: isStadium ? 'Open-air - dress for the evening temperature drop.' : 'Indoor and climate controlled.',
    },
    rules: {
      prohibited: ['Professional cameras', 'Large bags', 'Outside food & drink'],
      // No venue-specific bottle rule is known here, so no permission is invented: venues differ and
      // many (T-Mobile Arena) allow no bottles at all.
      waterPolicy: 'Bottle rules differ by venue and some allow no bottles at all - check the venue’s policy before you go.',
      // Rendered as its own sentence (PracticalInfoSection).
      payment: us ? 'Many US venues are cashless - bring a card.' : 'Many venues no longer take cash - bring a card.',
    },
    inside: {
      merchQueues: 'Merch lines are longest right at doors and during intermission.',
      foodDrinkPrices: us
        ? 'Concessions at major US venues are card-only - budget accordingly.'
        : 'Food and drink inside cost more than outside - budget accordingly.',
      wifi: 'Venue Wi-Fi exists but is usually overloaded - download tickets beforehand.',
    },
    soundView: {
      bestStanding: 'Near the sound booth usually has the most balanced audio.',
      soundQuality: 'Earplugs are worth bringing for loud shows.',
    },
  };
};


/** Layer REAL per-venue facts (roof, transit) over the generic guidance. */
const withVenueFacts = (info: PracticalInfo, e: Parameters<typeof venueSlugOf>[0] & { venue: string; date: string }): PracticalInfo => {
  const venue = e.venue;
  const slug = slugifyRaw(venue);
  const known = FACTS[slug];
  // a name two buildings share (Toyota Center: Houston and Kennewick): the facts are the one
  // on the plain URL (scripts/places.mjs pins it to the researched city)
  if (!known || venueSlugOf(e) !== slug) return info;
  // A venue rebuilt under the same name: the event's own date says which building it is in.
  const facts = known.next && e.date >= known.next.from ? known.next : known;
  const roofText: Record<string, [PracticalInfo['weather']['type'], string]> = {
    outdoor: ['Outdoor', `${venue} is open-air — dress for the weather.`],
    indoor: ['Indoor', `${venue} is fully indoor and climate controlled.`],
    retractable: ['Indoor', `${venue} has a retractable roof — usually closed in extreme weather.`],
    canopy: ['Outdoor', `${venue} has a canopy over the seats but is open to the air — dress for the temperature.`],
  };
  const [type, clothing] = roofText[facts.roof];
  return {
    ...info,
    weather: { ...info.weather, type, clothing },
    transport: facts.transit ? { ...info.transport, publicTransport: facts.transit } : info.transport,
  };
};

/** An MLB postseason game under the name people use ("World Series Game 1: Brewers home game", not
 *  "TBD at Brewers - World Series (Gm 1, HG 1)"; shared/postseason.mjs), so the heading, the opening
 *  line, cards, FAQs and strips all read it — the same name the edge puts in the title and schema
 *  (shared/slug.mjs displayName). A label stays a label, so a row converted twice is unchanged; the
 *  id and the slug (from the teams) never change. */
const plainName = (e: RawEvent): string | null => {
  if (e.category !== 'Sports' || e.artist) return null;
  const label = postseasonLabel(e.name);
  return label && label !== e.name ? label : null;
};

export const toEvent = (e: RawEvent & { practicalInfo?: PracticalInfo }): Event => {
  const name = plainName(e);
  return {
    // Carried-over entries from older builds may use the British spelling.
    ...(e.category === 'Theatre' ? { ...e, category: 'Theater' } : e),
    ...(name ? { name } : {}),
    // A row the prerender embedded already carries its facts-enriched info.
    practicalInfo: e.practicalInfo ?? withVenueFacts(getPracticalInfo(e), e),
    withFacts: !!e.practicalInfo || import.meta.env.SSR,
  };
};

/** Re-derive practical info once venue facts arrive in the browser. */
export const refreshPracticalInfo = (e: Event): Event => ({
  ...e,
  practicalInfo: withVenueFacts(getPracticalInfo(e), e),
  withFacts: true,
});

/** Column order of public/data/index.json rows (scripts/emit-data.mjs, store.ts toSlim).
 *  `country` is optional and last: written only when the venue is not in the US (the
 *  same rule as functions/events-meta.json), so older 18-column rows still read. */
export const SLIM_KEYS = [
  'id', 'name', 'artist', 'teams', 'date', 'time', 'venue', 'city', 'category',
  'kind', 'genre', 'lowestPrice', 'lowestAllIn', 'imageUrl', 'rank', 'lat', 'lng', 'dayChange', 'country',
] as const;

/** …optionally followed by the venue's country and the event's demand (list pages, /api/hub). */
type SlimRow = [
  string, string, string | null, string[] | null, string, string | null, string, string, string,
  string | null, string | null, number | null, 0 | 1, string | null, number | null, number | null, number | null, number | null,
  (string | null)?, (number | null)?,
];

/**
 * A slim index row → an Event good enough for cards, strips, maps and
 * filters. Marked `partial`: an event page must swap it for the full row
 * (offers, trend, description) before rendering the comparison.
 */
export const fromSlimRow = (r: SlimRow): Event => {
  const [id, name, artist, teams, date, time, venue, city, category, kind, genre, lowestPrice, lowestAllIn, imageUrl, rank, lat, lng, dayChange, country, demand] = r;
  const raw: RawEvent = {
    id,
    name,
    ...(artist ? { artist } : {}),
    ...(teams ? { teams } : {}),
    date,
    ...(time ? { time } : {}),
    venue,
    city,
    // A row that carries a country keeps it. Without one the row reads as US — true for
    // every row once the writers add the column for non-US venues (see SLIM_KEYS); until
    // then nothing that reads a slim row's country shows it to a visitor.
    country: country || 'US',
    category,
    ...(kind ? { kind } : {}),
    ...(genre ? { genre } : {}),
    description: '',
    imageUrl: imageUrl ?? '',
    lat: lat ?? 0,
    lng: lng ?? 0,
    venueType: 'Arena',
    ...(rank != null ? { rank } : {}),
    lowestPrice,
    ...(lowestAllIn ? { lowestAllIn: true } : {}),
    ...(dayChange != null ? { dayChange } : {}),
    ...(demand != null ? { demand } : {}),
  };
  return { ...toEvent(raw), partial: true };
};
